import * as React from "react";
import { IconClock, IconUsersGroup } from "@tabler/icons-react";
import { registrationRequest } from "../features/king-of-the-hill/registration.js";
import { countdownParts, remainingSeconds } from "../features/king-of-the-hill/countdown.js";
import "./KingOfTheHillPage.css";
import { KingHillGame } from "../features/king-of-the-hill/KingHillGame.jsx";
import { RegistrationSchedule } from "../features/king-of-the-hill/RegistrationSchedule.jsx";

export function KingOfTheHillPage() {
  const [state, setState] = React.useState(null);
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [refresh, setRefresh] = React.useState(0);
  const [preview, setPreview] = React.useState(false);
  const [clock, setClock] = React.useState(() => performance.now());
  const refreshedAtClose = React.useRef(false);
  const scheduleBusy = React.useRef(false);
  const revision = React.useRef(0);
  const acceptState = data => { const receivedAt = performance.now(); setState({ ...data, receivedAt }); setClock(receivedAt); };
  React.useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const expected = revision.current;
    const timeout = setTimeout(() => controller.abort(), 12000);
    setError("");
    registrationRequest("state", controller.signal).then(data => { if (active && expected === revision.current && !scheduleBusy.current) { acceptState(data); setError(""); } })
      .catch(error => { if (active && expected === revision.current && !scheduleBusy.current) { setError(error.name === "AbortError" ? "Сервер не ответил вовремя. Попробуйте ещё раз." : error.message); } })
      .finally(() => clearTimeout(timeout));
    return () => { active = false; clearTimeout(timeout); controller.abort(); };
  }, [refresh]);
  React.useEffect(() => {
    const timer = setInterval(() => setRefresh(value => value + 1), 60000);
    const visible = () => { if (!document.hidden) setRefresh(value => value + 1); };
    document.addEventListener("visibilitychange", visible);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", visible); };
  }, []);
  React.useEffect(() => {
    const timer = setInterval(() => setClock(performance.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const remaining = remainingSeconds(state, clock);
  React.useEffect(() => {
    if (remaining === null || remaining > 0) { refreshedAtClose.current = false; return; }
    if (state?.phase === "open" && !refreshedAtClose.current) { refreshedAtClose.current = true; setRefresh(value => value + 1); }
  }, [remaining, state?.phase]);
  async function join() {
    if (busy) return;
    setBusy(true); setError("");
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try { acceptState(await registrationRequest("register", controller.signal)); setRefresh(value => value + 1); }
    catch (error) { setError(error.name === "AbortError" ? "Сервер не ответил вовремя. Попробуйте ещё раз." : error.message); }
    finally { clearTimeout(timeout); setBusy(false); }
  }
  const status = !state ? (error ? "Регистрация недоступна" : "Проверяем регистрацию…") : remaining === 0 && state.phase === "open" ? "Проверяем начало…" : state.registered ? "Вы участвуете" :
    state.phase === "open" ? "Регистрация открыта" : state.phase === "upcoming" ? "Регистрация скоро" : "Регистрация закрыта";
  const registrationClosed = state?.phase === "closed" && !state.registered;
  const label = busy ? "Регистрируем…" : state?.registered ? "Вы участвуете" : "Участвовать";
  const deadlineLabel = Number.isFinite(state?.closes_at) ? new Intl.DateTimeFormat("ru-RU", { day:"numeric", month:"long", hour:"2-digit", minute:"2-digit", timeZone:"Europe/Moscow" }).format(new Date(state.closes_at * 1000)) + " МСК" : "время уточняется";
  if (preview && state?.can_preview) return <KingHillGame preview />;
  if (state?.registered && state.phase === "closed") return <KingHillGame />;
  return <section className="king-hill" aria-labelledby="king-hill-title">
    <h1 id="king-hill-title" className="king-hill__heading">Царь горы</h1>
    <div className="king-hill__scene">
    <div className="king-hill__art" role="img" aria-label="Тыквенная крепость и муравьи с фонарями" />
    <div className="king-hill__details">
      <h2>Тыквенная осада</h2>
      <span className="king-hill__status" role="status">{status}</span>
      <p className="king-hill__description">{registrationClosed ? <>Запись на это событие завершена.<br />Следите за новостями, чтобы не пропустить следующее.</> : <>Исследуй карту и сражайся<br />за Тыквенную гору</>}</p>
      {remaining !== null && state?.phase === "open" && <div className="king-hill__countdown" aria-label={`${state.registered ? "До начала режима" : "До закрытия регистрации"}: ${remaining} секунд`}>
        <IconClock aria-hidden="true" /><div><span>{state.registered ? "ДО НАЧАЛА РЕЖИМА" : "ДО ЗАКРЫТИЯ РЕГИСТРАЦИИ"}</span><strong>{(() => { const p = countdownParts(remaining); return <>{p.days > 0 && <>{p.days}<small>д</small> </>}{p.hours}<i>:</i>{p.minutes}<i>:</i>{p.seconds}</>; })()}</strong></div>
      </div>}
      <div className="king-hill__stats" aria-label="Информация о событии">
        {remaining === null && !registrationClosed && <div><IconClock aria-hidden="true" /><span>До {deadlineLabel}</span></div>}
        <div><IconUsersGroup aria-hidden="true" /><span>Участники: {state ? state.participants.toLocaleString("ru-RU") : "—"}</span></div>
      </div>
      {!registrationClosed && <button className="king-hill__join" type="button" disabled={!state || state.phase !== "open" || remaining === 0 || state.registered || busy} onClick={join}>{label}</button>}
      {state?.can_preview && <button type="button" className="king-hill__preview" onClick={() => setPreview(true)}>Открыть тестовый район</button>}
      {state?.can_preview && state.phase === "open" && Number.isFinite(state.closes_at) && <RegistrationSchedule state={state} onStart={() => { revision.current += 1; scheduleBusy.current = true; }} onUpdated={data => { acceptState(data); setError(""); }} onFinish={() => { scheduleBusy.current = false; setRefresh(value => value + 1); }} />}
      {error ? <p className="king-hill__note" role="alert">{error} <button type="button" className="king-hill__retry" onClick={() => setRefresh(value => value + 1)}>Повторить</button></p>
        : !registrationClosed && <p className="king-hill__note">{state?.registered ? "Вы зарегистрированы" : `Регистрация до ${deadlineLabel}`}</p>}
    </div>
    </div>
  </section>;
}
